import type { Address } from "viem";
import { configQuotaOf, type ExtensionManifest } from "./extension";

/**
 * The extension API, version 1 (D76): an extension's page, in a sandboxed iframe, sends requests to the Console over
 * postMessage and gets replies and events back. The Console holds the wallet, the keys and the sessions; the page
 * never does (D68). Every paid write settles on the extension's identity before the reply.
 *
 *   page → Console    { wp: 1, id, method, params }
 *   Console → page    { wp: 1, id, ok: true, result } | { wp: 1, id, ok: false, error: { code, message } }
 *   Console → page    { wp: 1, event, data }                (theme, caps, lang)
 */
export const EXTENSION_API_VERSION = 1;

export type ApiErrorCode =
  | "permission_denied" // the manifest does not declare what the method needs
  | "cap_exceeded" // the identity's cap in the token is used up
  | "balance" // the wallet holds too few tokens for the settle
  | "rejected" // the person rejected the signature
  | "unknown_method"
  | "no_store" // the manifest declares a store, but none was chosen at install
  | "bad_args"
  | "failed"; // the service did not answer, or answered with another error

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message?: string,
  ) {
    super(message ?? code);
  }
}

export interface ExtensionRequest {
  wp: 1;
  id: string;
  method: string;
  params?: Record<string, unknown>;
}

export type ExtensionReply =
  | { wp: 1; id: string; ok: true; result: unknown }
  | { wp: 1; id: string; ok: false; error: { code: ApiErrorCode; message: string } };

export type ExtensionEventName = "theme" | "caps" | "lang";

export interface ExtensionEvent {
  wp: 1;
  event: ExtensionEventName;
  data: Record<string, unknown>;
}

/** A message from the frame that is a request of the API; anything else is ignored. */
export function isExtensionRequest(d: unknown): d is ExtensionRequest {
  if (typeof d !== "object" || d === null) return false;
  const r = d as Partial<ExtensionRequest>;
  return (
    r.wp === 1 &&
    typeof r.id === "string" &&
    typeof r.method === "string" &&
    (r.params === undefined || (typeof r.params === "object" && r.params !== null))
  );
}

/** One cap of the extension's identity (D64): the chain's record, in whole tokens. */
export interface CapRow {
  token: string;
  cap: number;
  used: number;
  remaining: number;
}

/** What a paid write cost, as the reply carries it. */
export interface ApiReceipt {
  token: string;
  actual: string;
  tx: string;
}

export interface StoreFileView {
  name: string;
  size: number;
  type: string;
  updatedAt: string;
}

/** The answer to `ready`: what the page may know (D76). */
export interface ReadyResult {
  wallet: { address: Address; handle: string | null };
  extension: { id: string; name: string; version: string; identity: Address };
  lang: string;
  theme: "light" | "dark";
  permissions: ExtensionManifest["permissions"];
  caps: CapRow[];
  store: string | null;
  services: Array<{ name: string; token: string; price: string }>;
}

/** What the Console puts behind the methods; the keys, the sessions and the wallet stay on its side (D68). */
export interface ApiContext {
  manifest: ExtensionManifest;
  ready(): Promise<ReadyResult>;
  caps(): Promise<CapRow[]>;
  configGet(): Promise<Record<string, unknown>>;
  /** A paid write to the extension's namespace in the memory service (D74): the receipt of its settle. */
  configPut(config: Record<string, unknown>): Promise<ApiReceipt>;
  /** The store chosen at install, or null (D74). */
  store: null | {
    put(file: { name: string; size: number; type: string; bytes?: Uint8Array }): Promise<ApiReceipt>;
    list(): Promise<StoreFileView[]>;
    delete(name: string): Promise<void>;
  };
  /** A call to one of the manifest's services, paid by the extension's identity when the service charges it. */
  call(service: string, route: string, body: unknown): Promise<{ result: unknown; receipt: ApiReceipt | null }>;
  /** A cap for another identity, asked through the signing panel (D78). */
  walletRequest(p: {
    identity: string;
    label: string;
    token: string;
    cap: number;
    reason: string;
  }): Promise<{ signed: boolean; tx?: string }>;
  toast(text: string): void;
  balances(): Promise<Record<string, number>>;
  authorizations(): Promise<unknown>;
}

const enc = new TextEncoder();

async function dispatch(method: string, p: Record<string, unknown>, ctx: ApiContext): Promise<unknown> {
  const m = ctx.manifest;
  const need = (ok: boolean | undefined, what: string) => {
    if (!ok) throw new ApiError("permission_denied", what);
  };
  const store = () => {
    need(!!m.storage?.store, "the manifest declares no store");
    if (!ctx.store) throw new ApiError("no_store", "no store service was chosen at install");
    return ctx.store;
  };
  switch (method) {
    case "ready":
      return ctx.ready();
    case "config.get":
      need(!!m.storage?.config, "the manifest declares no configuration storage");
      return { config: await ctx.configGet() };
    case "config.put": {
      need(!!m.storage?.config, "the manifest declares no configuration storage");
      const config = p.config;
      if (typeof config !== "object" || config === null || Array.isArray(config))
        throw new ApiError("bad_args", "config must be an object");
      const quota = configQuotaOf(m);
      const bytes = enc.encode(JSON.stringify(config)).length;
      if (quota !== null && bytes > quota)
        throw new ApiError("bad_args", `the configuration is ${bytes} bytes, above the quota of ${quota}`);
      const receipt = await ctx.configPut(config as Record<string, unknown>);
      return { receipt, caps: await ctx.caps() };
    }
    case "store.put": {
      const s = store();
      const name = String(p.name ?? "");
      const size = Number(p.size ?? 0);
      const type = String(p.type ?? "application/octet-stream");
      if (!name || !Number.isFinite(size) || size < 0) throw new ApiError("bad_args", "name and size are required");
      const bytes = p.bytes instanceof Uint8Array ? p.bytes : undefined;
      const receipt = await s.put({ name, size, type, bytes });
      return { receipt, caps: await ctx.caps() };
    }
    case "store.list":
      return { entries: await store().list() };
    case "store.delete": {
      const name = String(p.name ?? "");
      if (!name) throw new ApiError("bad_args", "name is required");
      await store().delete(name);
      return {};
    }
    case "services.call": {
      const service = String(p.service ?? "");
      need(m.services?.includes(service), `${service || "(none)"} is not in the manifest's services`);
      const route = String(p.route ?? "");
      if (!route.startsWith("/")) throw new ApiError("bad_args", "route must start with /");
      const r = await ctx.call(service, route, p.body);
      return { result: r.result, receipt: r.receipt, caps: await ctx.caps() };
    }
    case "wallet.request": {
      need(m.permissions?.wallet?.includes("authorize"), "the manifest has no wallet permission");
      if (p.kind !== "authorize") throw new ApiError("bad_args", "only kind: authorize is supported");
      const identity = String(p.identity ?? "");
      const token = String(p.token ?? "");
      const cap = Math.floor(Number(p.cap ?? 0));
      if (!identity || !token || !(cap > 0))
        throw new ApiError("bad_args", "identity, token and a cap above 0 are required");
      return ctx.walletRequest({
        identity,
        label: String(p.label ?? identity),
        token,
        cap,
        reason: String(p.reason ?? ""),
      });
    }
    case "ui.toast":
      need(!!m.permissions?.notify, "the manifest has no notify permission");
      ctx.toast(String(p.text ?? ""));
      return {};
    case "read.balances":
      need(m.permissions?.read?.includes("balances"), "the manifest has no read: balances permission");
      return { balances: await ctx.balances() };
    case "read.authorizations":
      need(m.permissions?.read?.includes("authorizations"), "the manifest has no read: authorizations permission");
      return { groups: await ctx.authorizations() };
    default:
      throw new ApiError("unknown_method", `${method} is not a method of the extension API v${EXTENSION_API_VERSION}`);
  }
}

/**
 * One request of the API answered: the manifest's permissions checked first, then the Console's action; an error
 * becomes the reply's error with one of the codes (anything the action throws that is not an ApiError is `failed`).
 */
export async function handleExtensionRequest(req: ExtensionRequest, ctx: ApiContext): Promise<ExtensionReply> {
  try {
    const result = await dispatch(req.method, req.params ?? {}, ctx);
    return { wp: 1, id: req.id, ok: true, result };
  } catch (e) {
    const code: ApiErrorCode = e instanceof ApiError ? e.code : "failed";
    return { wp: 1, id: req.id, ok: false, error: { code, message: e instanceof Error ? e.message : String(e) } };
  }
}

/** A one-line summary of a call for the tab's log: the method's main argument and what it cost or returned. */
export function summarizeCall(method: string, p: Record<string, unknown>, reply: ExtensionReply): string {
  const main = (() => {
    if (method === "services.call") return `${String(p.service ?? "")} ${String(p.route ?? "")}`;
    if (method === "store.put" || method === "store.delete") return String(p.name ?? "");
    if (method === "wallet.request")
      return `${String(p.kind ?? "")} ${String(p.label ?? "")} · ${String(p.cap ?? "")} ${String(p.token ?? "")}`;
    if (method === "ui.toast") return JSON.stringify(String(p.text ?? "")).slice(0, 40);
    return "";
  })().trim();
  if (!reply.ok) return `${main} → ${reply.error.code}`.trim();
  const r = reply.result as { receipt?: ApiReceipt | null; signed?: boolean; tx?: string } | undefined;
  if (r?.receipt) return `${main} → ${r.receipt.actual} ${r.receipt.token} · tx ${r.receipt.tx}`.trim();
  if (typeof r?.signed === "boolean") return `${main} → ${r.signed ? `signed · tx ${r.tx ?? ""}` : "rejected"}`.trim();
  if (method === "ready" || method === "ui.toast" || method === "store.delete") return main;
  return `${main} → free`.trim();
}
